import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";
import { wtAppGoBack } from "../../../androidAndIosApp";
import { device } from "../../../constants/mediaQueries";
import { withWSErrorContainer } from "../../../error/withWSErrorContainer";
import { useLocStore } from "../../../localization";
import { LocContext } from "../../../localization/LocContext";
import { useSettings } from "../../../settings/useSettings";
import { getImageSrcFromSettingValue } from "../../../url/getImageSrcFromSettingValue";
import { useLogOut } from "../../../url/logOut";
import { getRedirectProps } from "../../../url/redirect";
import { optimizeImageUrl } from "../../../url/synchronizeCacheBusterParameter";
import { useLoginProps } from "../../../url/useLoginProps";
import { useWhoAmI } from "../../../user/useWhoAmI";
import { useDevice } from "../../../utils/style/useDevice";
import { MobileMenu, StyledMenuBtn } from "../MobileMenu";
import { ProfileMenu } from "../ProfileMenu";
import { useMediaQuery } from "../../../utils/style/useMediaQuery";
import { DarkModeToggleButton } from "../../../themes/DarkModeToggleButton";

const WtappBackButton = observer(() => {
  return (
    <StyledMenuBtn
      className="cvp-menu-btn"
      icon="pi pi-chevron-left"
      onClick={() => {
        wtAppGoBack();
      }}
    />
  );
});

export const TopBar = withWSErrorContainer(
  observer(() => {
    const { confirmedLoggedIn, concernId, whoAmI } = useWhoAmI();

    React.useEffect(() => {
      console.log(whoAmI);
    }, [whoAmI]);

    const { isTabletOrMobile } = useDevice();

    const showMenu = isTabletOrMobile && !!concernId && confirmedLoggedIn;

    return (
      <TopBarStyles className="cvp-app-top-bar">
        <StyledMenuLogoContainer className="cvp-menu-logo-container ">
          {!!window.iswtapp && <WtappBackButton />}
          {showMenu && <MobileMenu />}
          <LogoAndTitle />
        </StyledMenuLogoContainer>
        <StyledLogoutProfileContainer className="cvp-profile-container">
          <LoginOutButton />
          <DarkModeToggleButton />
          <ProfileMenu />
        </StyledLogoutProfileContainer>
      </TopBarStyles>
    );
  })
);

const LogoAndTitle = withWSErrorContainer(
  observer(() => {
    const settings = useSettings();
    const L = useLocStore(LocContext.GENERAL);

    const { whoAmI } = useWhoAmI();

    const logoSrc = getImageSrcFromSettingValue(
      settings.logoPrimary,
      window.cvpIdConcern
    );

    const concernName = whoAmI?.details?.concernName;

    return (
      <>
        <a
          {...getRedirectProps("/", { mergeQs: {} })}
          className="cvp-app-top-bar-logo-container"
          title={concernName ?? undefined}
        >
          {!!logoSrc && (
            <img
              src={optimizeImageUrl(logoSrc)}
              className="cvp-app-top-bar-logo"
              alt=""
            />
          )}

          <span className="cvp-app-top-bar-title">
            {!!concernName && L.id("ef9f7b8d5870").split("{0}", [concernName])}
            {!concernName && L.id("2c554f960185").loc("CV Portalen")}
          </span>
        </a>
      </>
    );
  })
);

const LoginOutButton = withWSErrorContainer(
  observer(() => {
    const L = useLocStore(LocContext.GENERAL);

    const { confirmedLoggedIn, confirmedNotLoggedIn } = useWhoAmI();
    const logOut = useLogOut();

    let { redirectProps: loginRedirectProps } = useLoginProps({
      cvpalru: "/profile",
    });

    const loginText = L.id("00923171353b").loc("Logg inn");
    const logoutText = L.id("55c552133c7f").loc("Logg ut");

    const isWideSurface = useMediaQuery(device.md);

    return (
      <>
        {!!confirmedNotLoggedIn && (
          <StyledLogInOutBtn
            className="p-button-text"
            aria-label={loginText}
            data-testid="top-bar-login-btn"
            {...loginRedirectProps}
            title={loginText}
          >
            <i className="pi pi-sign-in" aria-hidden="true" />
            {isWideSurface && loginText}
          </StyledLogInOutBtn>
        )}
        {!!confirmedLoggedIn && isWideSurface && (
          <StyledLogInOutBtn
            className="p-button-text"
            aria-label={logoutText}
            data-testid="top-bar-logout-btn"
            onClick={logOut}
            title={logoutText}
          >
            <i className="pi pi-sign-out" aria-hidden="true" />
            {logoutText}
          </StyledLogInOutBtn>
        )}
      </>
    );
  })
);

const StyledLogInOutBtn = styled.a`
  &&&&& {
    text-decoration: none;
    font-size: 0.9em;
    font-weight: 600;
    color: var(--primary-500);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const StyledMenuLogoContainer = styled.div`
  &.cvp-menu-logo-container {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    column-gap: ${(p) => (window.iswtapp ? "0" : "0.5rem")};

    button {
      flex-shrink: 0;
    }

    .cvp-app-top-bar-logo-container {
      display: flex;
      align-items: center;
      flex: 1 1 auto;
      min-width: 0;
      padding: 0.2rem 0.3rem;
      gap: 0.5rem;
      text-decoration: none;
      overflow: hidden;

      :hover {
        opacity: 0.8;
      }
    }

    .cvp-app-top-bar-logo {
      flex-shrink: 0;
      max-height: 1.1rem;
      max-width: auto;
      width: auto;
      height: auto;
      object-fit: contain;

      @media ${device.md} {
        max-height: 1.35rem;
        max-width: auto;
      }
    }

    .cvp-app-top-bar-title {
      flex: 1 1 auto;
      min-width: 0;
      font-size: 0.85em;
      font-weight: 600;
      color: var(--text-color);
      line-height: 1.25;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      @media ${device.md} {
        font-size: 0.95em;
      }
    }
  }
`;

const StyledLogoutProfileContainer = styled.div`
  &.cvp-profile-container {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
`;

const TopBarStyles = styled.div`
  &.cvp-app-top-bar {
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 1100;

    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--surface-a);
    padding: ${(p) => (window.iswtapp ? "0 1rem 0 0" : "0 1rem")};
    height: 3rem;
    box-sizing: border-box;
    border-bottom: 1px solid var(--primary-100-relative);

    .cvp-avatar-button {
      padding: 0;
    }

    .cvp-avatar {
      margin-left: 0.75rem;
      color: var(--primary-500);
      background-color: var(--primary-100-relative);
    }
  }
`;
